import { useLocation } from 'react-router-dom';
import { Layout } from '../../components/layout/Layout';
import CatalogCRUD from '../../components/config/CatalogCRUD';
import { usePermissions } from '../../hooks/usePermissions';
import {
  AlertTriangle,
  Box,
  Clock,
  Layers,
  Cloud,
  Monitor,
  Timer,
  Plug,
  Link2,
  GitBranch,
  Shield,
  Tag,
  Cpu,
  AppWindow,
  CircleDot,
  Users,
  Database
} from 'lucide-react';

// Configuración de cada catálogo
const CATALOG_CONFIG = {
  'criticidad': {
    catalogKey: 'criticidad',
    title: 'Criticidad',
    icon: AlertTriangle,
    nameLabel: 'Nivel',
    pageKey: 'config.criticidad'
  },
  'tipos-dispositivo': {
    catalogKey: 'tipos-dispositivo',
    title: 'Tipos de Dispositivo',
    icon: Box,
    nameLabel: 'Tipo',
    pageKey: 'config.tipos_dispositivo'
  },
  'tipos-vencimiento': {
    catalogKey: 'tipos-vencimiento',
    title: 'Tipos de Vencimiento',
    icon: Clock,
    nameLabel: 'Tipo',
    pageKey: 'config.tipos_vencimiento'
  },
  'ambientes': {
    catalogKey: 'ambientes',
    title: 'Ambientes',
    icon: Layers,
    nameLabel: 'Ambiente',
    pageKey: 'config.ambientes'
  },
  'cloud': {
    catalogKey: 'cloud',
    title: 'Cloud',
    icon: Cloud,
    nameLabel: 'Cloud',
    pageKey: 'config.cloud'
  },
  'sistemas-operativos': {
    catalogKey: 'sistemas-operativos',
    title: 'Sistemas Operativos',
    icon: Monitor,
    nameLabel: 'Sistema Operativo',
    pageKey: 'config.sistemas_operativos'
  },
  'ventanas-reinicio': {
    catalogKey: 'ventanas-reinicio',
    title: 'Ventanas de Reinicio',
    icon: Timer,
    nameLabel: 'Ventana',
    pageKey: 'config.ventanas_reinicio'
  },
  'tipos-conexion': {
    catalogKey: 'tipos-conexion',
    title: 'Tipos de Conexion',
    icon: Plug,
    nameLabel: 'Tipo',
    pageKey: 'config.tipos_conexion'
  },
  'tipos-enlace': {
    catalogKey: 'tipos-enlace',
    title: 'Tipos de Enlace',
    icon: Link2,
    nameLabel: 'Tipo',
    pageKey: 'config.tipos_enlace'
  },
  'roles-enlace': {
    catalogKey: 'roles-enlace',
    title: 'Roles de Enlace',
    icon: GitBranch,
    nameLabel: 'Rol',
    pageKey: 'config.roles_enlace'
  },
  'modelos-firewalls': {
    catalogKey: 'modelos-firewalls',
    title: 'Modelos de Firewalls',
    icon: Shield,
    nameLabel: 'Modelo',
    pageKey: 'config.modelos_firewalls',
    extraFields: ['last_firmware']
  },
  'marcas': {
    catalogKey: 'marcas',
    title: 'Marcas',
    icon: Tag,
    nameLabel: 'Marca',
    pageKey: 'config.marcas'
  },
  'modelos-switches': {
    catalogKey: 'modelos-switches',
    title: 'Modelos de Switches',
    icon: Cpu,
    nameLabel: 'Modelo',
    pageKey: 'config.modelos_switches',
    extraFields: ['last_firmware']
  },
  // Apps - Catálogos del módulo Mapa de Aplicaciones
  'app-types': {
    catalogKey: 'app-types',
    title: 'Tipos de Aplicación',
    icon: AppWindow,
    nameLabel: 'Tipo',
    pageKey: 'config.app_types'
  },
  'app-status': {
    catalogKey: 'app-status',
    title: 'Estados de Aplicación',
    icon: CircleDot,
    nameLabel: 'Estado',
    pageKey: 'config.app_status',
    extraFields: ['color']
  },
  'app-roles': {
    catalogKey: 'app-roles',
    title: 'Roles de Responsable',
    icon: Users,
    nameLabel: 'Rol',
    pageKey: 'config.app_roles'
  },
  'app-interface-types': {
    catalogKey: 'app-interface-types',
    title: 'Tipos de Interfaz',
    icon: Plug,
    nameLabel: 'Tipo',
    pageKey: 'config.app_interface_types',
    extraFields: ['icon']
  },
  'app-db-types': {
    catalogKey: 'app-db-types',
    title: 'Tipos de Base de Datos',
    icon: Database,
    nameLabel: 'Tipo BD',
    pageKey: 'config.app_db_types'
  }
};

const CatalogPage = () => {
  const location = useLocation();
  // Extract catalog key from path: /config/tipos-dispositivo -> tipos-dispositivo
  const catalogKey = location.pathname.split('/').pop();
  const config = CATALOG_CONFIG[catalogKey];
  
  // Hooks must be called unconditionally
  const permKey = catalogKey ? catalogKey.replace(/-/g, '_') : 'unknown';
  const { canCreate, canUpdate, canDelete } = usePermissions('config', permKey);
  
  // Si no existe la configuración, mostrar error
  if (!config) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Catalogo no encontrado: {catalogKey}</p>
        </div>
      </Layout>
    );
  }

  const Icon = config.icon;

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="page-header">
          <div className="flex items-center gap-3">
            <Icon className="h-8 w-8 text-primary" strokeWidth={1.5} />
            <div>
              <h1 className="page-title">{config.title}</h1>
              <p className="page-subtitle">Configuracion de {config.title.toLowerCase()}</p>
            </div>
          </div>
        </div>

        {/* CRUD Component */}
        <CatalogCRUD
          catalogKey={config.catalogKey}
          title={config.title}
          icon={config.icon}
          nameLabel={config.nameLabel}
          extraFields={config.extraFields || []}
          canCreate={canCreate}
          canUpdate={canUpdate}
          canDelete={canDelete}
        />
      </div>
    </Layout>
  );
};

export default CatalogPage;
